import { memo, useCallback } from 'react';

import type { JobSummaryViewModel } from '@/features/job-improved-selection';
import type { RjCommBulkResult } from '@/widgets/rj-comm-bulk';
import { RjCommBulkWidget } from '@/widgets/rj-comm-bulk';
import { jobSummaryToVacancy } from '@/shared/lib/job-summary-to-vacancy';
import { moveCandidateToContacted } from '@/shared/lib/move-candidate-to-contacted';
import { rjActivityLogsKey } from '@/shared/legacy/hooks/useRJActivityLogsQuery';
import { queryClient } from 'config/queryClient';
import { resetCampaignCandidatesQueryIfCurrentPageEmpty } from 'services/query-cache/updates';
import { CANDIDATE_STATUS } from 'services/candidates';
import { GlobalDialogType } from '@/stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from '@/stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from '@/stores/global-dialogs/hooks/useIsGlobalDialogOpen';

export const RjCommBulkModal = memo(() => {
  const rjCommBulkModal = useGlobalDialog(GlobalDialogType.RjCommBulk);
  const enrichVacancyModal = useGlobalDialog(GlobalDialogType.EnrichVacancy);
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.RjCommBulk);
  const dialogData = useGlobalDialogData({ dialogType: GlobalDialogType.RjCommBulk, isVisible });

  const handleEditJob = useCallback(
    (job: JobSummaryViewModel) => {
      enrichVacancyModal.open({ payload: { vacancy: jobSummaryToVacancy(job) } });
    },
    [enrichVacancyModal],
  );

  const campaignId = dialogData?.campaignId;
  const handleStartCandidate = useCallback(
    (esPersonId: string) => {
      moveCandidateToContacted(esPersonId, CANDIDATE_STATUS.NEW);
      if (campaignId == null) return;
      queryClient.invalidateQueries(rjActivityLogsKey({ es_person_id: esPersonId, campaign_id: campaignId }));
    },
    [campaignId],
  );

  const handleBulkSendComplete = useCallback(
    (result: RjCommBulkResult) => {
      if (dialogData == null) return;
      resetCampaignCandidatesQueryIfCurrentPageEmpty(dialogData.campaignId);
      dialogData.onComplete?.(result);
    },
    [dialogData],
  );

  if (!dialogData) {
    return null;
  }

  return (
    <RjCommBulkWidget
      isVisible={isVisible}
      onClose={rjCommBulkModal.close}
      {...dialogData}
      onComplete={handleBulkSendComplete}
      onStartCandidate={handleStartCandidate}
      onEditJob={handleEditJob}
    />
  );
});

RjCommBulkModal.displayName = 'RjCommBulkModal';
